const SALT_LEN = 16;
const IV_LEN = 12;
const PBKDF2_ITERATIONS = 100000;

/**
 * Decrypt content.encrypted (format: salt + iv + ciphertext + authTag) with password.
 * Uses PBKDF2 + AES-GCM to match scripts/encrypt-charley-memory.js.
 * @param {ArrayBuffer} encryptedBuffer
 * @param {string} password
 * @returns {Promise<Array>} parsed JSON array of memory entries
 */
export const decryptContent = async (encryptedBuffer, password) => {
  if (!window.crypto?.subtle) throw new Error('crypto.subtle not available');
  const bytes = new Uint8Array(encryptedBuffer);
  if (bytes.length < SALT_LEN + IV_LEN + 16) throw new Error('Invalid encrypted data');

  const salt = bytes.slice(0, SALT_LEN);
  const iv = bytes.slice(SALT_LEN, SALT_LEN + IV_LEN);
  const ciphertext = bytes.slice(SALT_LEN + IV_LEN);

  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const keyBits = await window.crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    256
  );
  const key = await window.crypto.subtle.importKey(
    'raw',
    keyBits,
    'AES-GCM',
    false,
    ['decrypt']
  );
  const plain = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );
  const text = new TextDecoder().decode(plain);
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    const msg = e && typeof e === 'object' && 'message' in e ? e.message : '';
    throw new Error(`Invalid content JSON${msg ? `: ${msg}` : ''}`);
  }
  if (!Array.isArray(data)) throw new Error('Invalid content format');
  return data;
};
